import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { LicenceResponse } from '@shared/schemas';

import type { LicenceDraft } from '../domain/licence-draft.model';
import type { MyLicenceError } from '../domain/my-licence-error';
import { initialState } from './my-licence.state';

/**
 * My Licence slice. Reducers are pure state transitions only; the I/O (load the
 * current licence, persist the rename) lives in the saga, which dispatches these
 * `load*` / `save*` transitions as it progresses. This is what lets the reducers be
 * unit-tested as `(state, action) => state` with zero mocks.
 */
const slice = createSlice({
  name: 'myLicence',
  initialState,
  reducers: {
    // ── Load lifecycle (dispatched by the saga) ──
    loadStarted: (state) => {
      state.loadStatus = 'loading';
      state.error = null;
    },
    loadSucceeded: (state, action: PayloadAction<LicenceResponse>) => {
      state.licence = action.payload;
      state.draft = { name: action.payload.name };
      state.loadStatus = 'loaded';
    },
    loadFailed: (state, action: PayloadAction<MyLicenceError>) => {
      state.loadStatus = 'failed';
      state.error = action.payload;
    },

    /** The name field changed; clears its validation error. */
    fieldChanged: (state, action: PayloadAction<{ field: keyof LicenceDraft; value: string }>) => {
      const { field, value } = action.payload;
      state.draft[field] = value;
      delete state.validationErrors[field];
    },

    // ── Save lifecycle ──
    saveStarted: (state) => {
      state.saveStatus = 'saving';
      state.error = null;
      state.validationErrors = {};
    },
    // On success, patch the loaded licence in place so the header/draft reflect the
    // new name (the page also syncs the app-wide AuthContext copy).
    saveSucceeded: (state, action: PayloadAction<LicenceResponse>) => {
      state.saveStatus = 'succeeded';
      state.licence = action.payload;
      state.draft = { name: action.payload.name };
    },
    saveFailed: (state, action: PayloadAction<MyLicenceError>) => {
      state.saveStatus = 'failed';
      state.error = action.payload;
      if (action.payload.kind === 'validation') {
        state.validationErrors = action.payload.fields;
      }
    },

    /** Reset everything to the initial state (on unmount / navigation away). */
    resetForm: () => initialState,
  },
});

export const myLicenceActions = slice.actions;
export const myLicenceReducer = slice.reducer;
